import { defineStore } from "pinia";

// Libraries
import axios from "axios";

// Utilities
import { sdk } from "@/js/sdk";
import { extractSdkErrorMessage } from "@/js/sdkError";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";
import * as dsUtil from "@/components/DataSources/utilities";
import { monitoringCaptureError } from "@/plugins/monitoring";

// Types
import { Repository } from "@/types/api/repositories";
import { RepositoryConnectionRequestConfig } from "@/js/signaloidClient.types";
import { isLocalMountConfig, LocalMountConfig } from "@/types/api/dataSources";
import { RemoteAccessState } from "@/types/general";

// Stores
import { useDataSourcesStore } from "./dataSources";

type StoreState = {
	connectedRepositories: undefined | Repository[];
	remoteRepositoryUpdateStatus: RemoteAccessState;
};

/*
 * Maps an error thrown by sdk.repositories.update into the three
 * states the UI surfaces via RemoteAccessState. The SDK throws SdkError
 * for any non-2xx HTTP response; axios errors only leak through when the
 * SDK couldn't get a response at all (network down, DNS failure, etc).
 * Anything else (defensive) is the "unknown" branch.
 */
function applyRepoUpdateError(
	error: unknown,
	updateStatus: RemoteAccessState,
	serverErrorMessage: string,
	networkErrorMessage: string,
	unknownErrorMessage: string,
) {
	// Only ever called from a catch, so every branch is a failure: flag the
	// error unconditionally and vary only the message by error class.
	updateStatus.error = true;
	if (error instanceof SdkError) {
		updateStatus.message = extractSdkErrorMessage(error) || serverErrorMessage;
	} else if (axios.isAxiosError(error)) {
		updateStatus.message = networkErrorMessage;
	} else {
		updateStatus.message = unknownErrorMessage;
	}
}

export const useRepositoriesStore = defineStore("repositories", {
	state: (): StoreState => ({
		connectedRepositories: undefined,
		remoteRepositoryUpdateStatus: { loading: false, error: false, message: "" },
	}),

	actions: {
		async fetchRepositoryByID(repositoryID: string) {
			try {
				const response = await sdk.repositories.getOne(repositoryID);
				const repository = response as Repository;

				if (this.connectedRepositories === undefined) {
					this.connectedRepositories = [];
				}

				const idx = this.connectedRepositories.findIndex((r) => r.RepositoryID === repository.RepositoryID);
				if (idx === -1) {
					this.connectedRepositories.push(repository);
				} else {
					this.connectedRepositories[idx] = repository;
				}

				return repository;
			} catch (error) {
				if (
					error instanceof SdkError &&
					error.code === ERROR_CODES.API_NOT_FOUND &&
					this.connectedRepositories
				) {
					const idx = this.connectedRepositories.findIndex((r) => r.RepositoryID === repositoryID);
					if (idx !== -1) {
						this.connectedRepositories.splice(idx, 1);
					}
				}
				throw error;
			}
		},
		async getRepositoryByID(repositoryID: string) {
			if (this.connectedRepositories) {
				// if connectedRepositories is truthy, there is at least one repo in memory
				const repoInMemory = this.connectedRepositories.find((repoInMemory) => {
					return repoInMemory.RepositoryID === repositoryID;
				});
				if (repoInMemory) {
					return repoInMemory;
				}
			}
			// For now, always fetch from remote as a fallback
			const repository = await this.fetchRepositoryByID(repositoryID);
			return repository;
		},
		updateRepoInMemoryByID(repositoryID: string, repositoryItem: Repository) {
			if (this.connectedRepositories) {
				// if connectedRepositories is truthy, there is at least one repo in memory
				const idOfRepoInMemory = this.connectedRepositories.findIndex((repoInMemory) => {
					return repoInMemory.RepositoryID === repositoryID;
				});
				if (idOfRepoInMemory !== -1) {
					// this.connectedRepositories[idOfRepoInMemory] = repositoryItem;
					// See https://v2.vuejs.org/v2/guide/reactivity.html#For-Arrays
					this.connectedRepositories[idOfRepoInMemory] = repositoryItem;
				}
			}
		},

		async connectToRepository(repoRequestConfig: RepositoryConnectionRequestConfig) {
			return sdk.repositories.connect(repoRequestConfig);
		},
		async disconnectFromRepository(repositoryID: string) {
			return sdk.repositories.disconnect(repositoryID);
		},
		async updateRepositoryConfig(
			repositoryID: string,
			repoRequestConfig: Partial<RepositoryConnectionRequestConfig>,
		) {
			return sdk.repositories.update(repositoryID, repoRequestConfig);
		},
		async removeRepositoryConfigKey(
			repositoryID: string,
			keysToRemove: (keyof RepositoryConnectionRequestConfig)[],
		) {
			return sdk.repositories.update(repositoryID, {}, { remove: keysToRemove });
		},
		async fetchRemoteRepositories() {
			//TODO: add pagination
			this.remoteRepositoryUpdateStatus.loading = true;
			try {
				const response = await sdk.repositories.list();
				this.connectedRepositories = response.Repositories as Repository[];
				this.remoteRepositoryUpdateStatus.error = false;
			} catch (error) {
				this.remoteRepositoryUpdateStatus.error = true;
				monitoringCaptureError(error, "Get user repository list");
			} finally {
				this.remoteRepositoryUpdateStatus.loading = false;
			}
		},

		/*
		 * Repository update functions
		 */
		async setRepositoryCoreID(repositoryID: string, coreID: string, updateStatus: RemoteAccessState) {
			// The update status is a reference to a RemoteAccessState object which will be mutated inside this function to set the loading state and also error states
			try {
				updateStatus.loading = true;
				const partialRepoConfigToUpdate: Partial<RepositoryConnectionRequestConfig> = {
					Core: coreID,
				};
				const response = await this.updateRepositoryConfig(repositoryID, partialRepoConfigToUpdate);
				this.updateRepoInMemoryByID(repositoryID, response as Repository);
				updateStatus.loading = false;
				return response as Repository;
			} catch (error) {
				monitoringCaptureError(error, "Set repository core");
				applyRepoUpdateError(
					error,
					updateStatus,
					"An error occurred while updating the core. Please try again.",
					"Failed to update the core. Make sure you have an active internet connection.",
					"Could not update core. Unknown error occurred",
				);
				updateStatus.loading = false;
				throw error;
			}
		},
		async setRepositoryWorkingBranch(
			repositoryID: string,
			branchName: string,
			commitHash: string,
			updateStatus: RemoteAccessState,
		) {
			// The update status is a reference to a RemoteAccessState object which will be mutated inside this function to set the loading state and also error states
			try {
				updateStatus.loading = true;
				const partialRepoConfigToUpdate: Partial<RepositoryConnectionRequestConfig> = {
					Branch: branchName,
					Commit: commitHash,
				};

				const response = await this.updateRepositoryConfig(repositoryID, partialRepoConfigToUpdate);
				this.updateRepoInMemoryByID(repositoryID, response as Repository);
				updateStatus.loading = false;
				return response as Repository;
			} catch (error) {
				monitoringCaptureError(error, "Set repository working directory");
				applyRepoUpdateError(
					error,
					updateStatus,
					"An error occurred while updating the working branch. Please try again.",
					"Failed to update the working branch. Make sure you have an active internet connection.",
					"Could not update the working branch. Unknown error occurred",
				);
				updateStatus.loading = false;
				throw error;
			}
		},
		async setRepositoryRunArguments(repositoryID: string, runArguments: string, updateStatus: RemoteAccessState) {
			// The update status is a reference to a RemoteAccessState object which will be mutated inside this function to set the loading state and also error states
			try {
				updateStatus.loading = true;
				const partialRepoConfigToUpdate: Partial<RepositoryConnectionRequestConfig> = {
					Arguments: runArguments,
				};

				const response = await this.updateRepositoryConfig(repositoryID, partialRepoConfigToUpdate);
				this.updateRepoInMemoryByID(repositoryID, response as Repository);
				updateStatus.loading = false;
				return response as Repository;
			} catch (error) {
				monitoringCaptureError(error, "Set repository run arguments");
				applyRepoUpdateError(
					error,
					updateStatus,
					"An error occurred while updating the runtime arguments. Please try again.",
					"Failed to update the runtime arguments. Make sure you have an active internet connection.",
					"Could not update the runtime arguments. Unknown error occurred",
				);
				updateStatus.loading = false;
				throw error;
			}
		},
		async setRepositoryBuildDirectory(
			repositoryID: string,
			buildDirectory: string,
			updateStatus: RemoteAccessState,
		) {
			// The update status is a reference to a RemoteAccessState object which will be mutated inside this function to set the loading state and also error states
			// TODO: Stop doing the above. Return values exist for this reason.
			try {
				updateStatus.loading = true;
				const partialRepoConfigToUpdate: Partial<RepositoryConnectionRequestConfig> = {
					BuildDirectory: buildDirectory,
				};

				const response = await this.updateRepositoryConfig(repositoryID, partialRepoConfigToUpdate);
				this.updateRepoInMemoryByID(repositoryID, response as Repository);
				updateStatus.loading = false;
				return response as Repository;
			} catch (error) {
				monitoringCaptureError(error, "Set repository build directory");
				applyRepoUpdateError(
					error,
					updateStatus,
					"An error occurred while updating the build directory. Please try again.",
					"Failed to update the build directory. Make sure you have an active internet connection.",
					"Could not update the build directory. Unknown error occurred",
				);
				updateStatus.loading = false;
				throw error;
			}
		},
		async setRepositoryDataSources(
			repositoryID: string,
			mountConfig: null | undefined | LocalMountConfig,
			updateStatus: RemoteAccessState,
		) {
			// The update status is a reference to a RemoteAccessState object which will be mutated inside this function to set the loading state and also error states
			try {
				updateStatus.loading = true;
				let partialRepoConfigToUpdate: undefined | Partial<RepositoryConnectionRequestConfig> = undefined;

				if (mountConfig === null) {
					// if mountConfig = null it means we want to disable mounting
					partialRepoConfigToUpdate = {
						DataSources: [],
					};
				} else {
					// if mountConfig is not valid or the user requested to reset to default
					if (!isLocalMountConfig(mountConfig) || mountConfig === undefined) {
						// if invalid mount config try to set to the default configs
						const dataSourcesStore = useDataSourcesStore();
						mountConfig =
							(await dataSourcesStore.getGlobalDefaultMountConfig()) ??
							(await dataSourcesStore.getFallbackMountConfig());
					}

					// construct the partial repo config object
					partialRepoConfigToUpdate = {
						DataSources: await dsUtil.formatDataSourcesPayload(
							mountConfig?.ResourceID,
							mountConfig?.ResourceType,
							mountConfig?.Location,
						),
					};
				}

				const response = await this.updateRepositoryConfig(repositoryID, partialRepoConfigToUpdate);
				this.updateRepoInMemoryByID(repositoryID, response as Repository);
				updateStatus.loading = false;
				return response as Repository;
			} catch (error) {
				monitoringCaptureError(error, "Set repository data sources");
				applyRepoUpdateError(
					error,
					updateStatus,
					"An error occurred while updating the data sources. Please try again.",
					"Failed to update the data sources. Make sure you have an active internet connection.",
					"Could not update the data sources. Unknown error occurred",
				);
				updateStatus.loading = false;
				throw error;
			}
		},
	},
	getters: {
		getConnectedRepositories: (state) => {
			if (!state.connectedRepositories) {
				return [] as Repository[];
			}
			return state.connectedRepositories;
		},
		isURLConnected:
			(state) =>
			(remoteURL: string): boolean => {
				return state.connectedRepositories?.some((repo) => repo.RemoteURL === remoteURL) ?? false;
			},
		loadError: (state): boolean => state.remoteRepositoryUpdateStatus.error,
	},
});
